
/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * @param {string | number | null | undefined} value
 * @returns {string}
 */
function normalizePlanId(value) {
  return String(value ?? '').trim();
}

/**
 * @param {PlanAllocation} allocation
 * @param {string | number | null | undefined} sellingPlanId
 * @returns {boolean}
 */
/**
 * Plan descriptions are admin-authored HTML (Loop) that lands in the card via
 * innerHTML. Strip anything executable before it does: active elements, event
 * handler attributes and script-scheme URLs. Presentation markup (b, a, ul,
 * br, inline style) is left alone so existing descriptions render as before.
 * @param {Element} root
 */
function sanitizeDescriptionTree(root) {
  const blockedTags = /^(SCRIPT|STYLE|IFRAME|OBJECT|EMBED|LINK|META|FORM|INPUT|BUTTON|TEXTAREA|SELECT|SVG|MATH|TEMPLATE|BASE)$/i;
  const urlAttributes = /^(href|src|xlink:href|action|formaction|poster)$/;
  root.querySelectorAll('*').forEach((el) => {
    if (blockedTags.test(el.tagName)) {
      el.remove();
      return;
    }
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = String(attr.value).replace(/\s+/g, '').toLowerCase();
      if (name.startsWith('on') || name === 'srcdoc') {
        el.removeAttribute(attr.name);
      } else if (urlAttributes.test(name) && /^(javascript|vbscript|data):/.test(value)) {
        el.removeAttribute(attr.name);
      }
    });
  });
}

function allocationMatchesSellingPlanId(allocation, sellingPlanId) {
  const normalizedId = normalizePlanId(sellingPlanId);
  return (
    normalizePlanId(allocation.sellingPlanId) === normalizedId ||
    normalizePlanId(allocation.sellingPlanIdNumeric) === normalizedId
  );
}

/**
 * True for a selling plan created by a BNPL PROVIDER app (Klarna, Affirm,
 * Afterpay, Sezzle, Quadpay, Zip, Shop Pay Installments), matched by brand name
 * only. Deliberately NOT matched: generic payment wording such as
 * "installment", "split pay" or "4 payments" - a Loop plan a merchant names
 * "3 Installments Plan" is a real selling plan and must resolve like any other.
 * The Liquid `recurring_deliveries` gate already keeps real BNPL-app plans out
 * of the payload; this is a second line of defence. Keep the list in sync with
 * the Liquid seed loop in snippets/channel-selling-plan-selector.liquid.
 * @param {string} name
 * @returns {boolean}
 */
function isInstallmentPlan(name) {
  const n = normalizeKey(name);
  return /\b(klarna|affirm|after\s*pay|shop\s*pay\s*install|sezzle|quadpay|zip\s*pay)\b/.test(n);
}

/**
 * @typedef {Object} PlanAllocation
 * @property {string | number} sellingPlanId
 * @property {string} sellingPlanIdNumeric
 * @property {string} sellingPlanName
 * @property {string} deliveryFrequency
 * @property {string | null} description
 * @property {boolean} [isDefault]
 * @property {number} [position]
 * @property {string} groupId
 * @property {string} groupName
 * @property {number} price
 * @property {number | null} compareAtPrice
 */

/**
 * @typedef {Object} BundleVariantMatch
 * @property {string | number} id
 * @property {number} price
 * @property {number | null} compareAtPrice
 */

/**
 * @typedef {Object} VariantPlansPayload
 * @property {Record<string, PlanAllocation[]>} variants
 * @property {Record<string, number>} [basePrices]
 * @property {Record<string, number | null>} [baseCompareAtPrices]
 * @property {Record<string, string[]>} [groupPlanOrder]
 * @property {{ id: string; name: string }[]} groups
 * @property {Record<string, BundleVariantMatch>} [bundleVariants]
 */

/**
 * @param {PlanAllocation[]} allocations
 * @returns {PlanAllocation[]}
 */
function filterRecurringAllocations(allocations) {
  return allocations.filter((a) => !isInstallmentPlan(a.sellingPlanName));
}

/**
 * @param {PlanAllocation[]} allocations
 * @returns {PlanAllocation | undefined}
 */
function pickCheapestAllocation(allocations) {
  const usable = filterRecurringAllocations(allocations);
  if (!usable.length) return undefined;
  return usable.reduce((best, cur) => (Number(cur.price) < Number(best.price) ? cur : best));
}

class ChannelSellingPlanRoot extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {(() => void) | undefined} */
  #variantChangeUnsubscriber;

  /** @type {string | null} Stored original variant ID when bundle swaps the form input */
  #savedOriginalVariantId = null;

  /** @type {Record<string, object> | null} Parsed once; the payload is static. */
  #companionMap = null;

  /** @type {VariantPlansPayload | null} */
  #payload = null;

  /** @type {(event: Event) => void} */
  #onVariantUpdateBound = this.#onVariantUpdate.bind(this);

  /** @type {(event: Event) => void} */
  #onPurchaseTypeChangeBound = this.#onPurchaseTypeChange.bind(this);

  /** @type {(event: Event) => void} */
  #onSelectChangeBound = this.#onSelectChange.bind(this);

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#section = this.closest('.shopify-section, dialog');
    this.#form = this.closest('form');
    this.#payload = this.#readPayload();

    const pubsub = window.theme?.pubsub;
    if (pubsub?.subscribe && pubsub.PUB_SUB_EVENTS?.variantChange) {
      this.#variantChangeUnsubscriber = pubsub.subscribe(
        pubsub.PUB_SUB_EVENTS.variantChange,
        this.#onVariantUpdateBound
      );
    }

    this.#form?.addEventListener('variant:change', this.#onVariantUpdateBound, {
      signal: this.#abortController.signal,
    });

    const purchaseInputs = this.querySelectorAll('[data-channel-purchase-type]');
    for (const input of purchaseInputs) {
      input.addEventListener('change', this.#onPurchaseTypeChangeBound, {
        signal: this.#abortController.signal,
      });
    }

    this.querySelector('[data-channel-selling-plan-select]')?.addEventListener(
      'change',
      this.#onSelectChangeBound,
      { signal: this.#abortController.signal }
    );

    void this.#init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#variantChangeUnsubscriber?.();
    this.#variantChangeUnsubscriber = undefined;
    this.#section = null;
    this.#form = null;
    this.#payload = null;
    this.#companionMap = null;
    this.#savedOriginalVariantId = null;
  }

  /**
   * @returns {VariantPlansPayload | null}
   */
  #readPayload() {
    const el = this.querySelector('script[data-channel-selling-plan-variants]');
    if (!el?.textContent) return null;
    try {
      return /** @type {VariantPlansPayload} */ (JSON.parse(el.textContent));
    } catch {
      return null;
    }
  }

  async #init() {
    await this.#refreshPlanState();
    // Only now does the module own the companion inputs. Until the first
    // refresh has run, the Liquid-seeded filter_variant_id still names the
    // load-time variant's filter, so the inline pre-hydration script must keep
    // disabling the inputs on a variant change (the config fetch above can be
    // slow enough for a shopper to switch variants inside it).
    this.dataset.channelHydrated = 'true';
  }

  /**
   * @returns {string}
   */
  #currentVariantId() {
    const fromForm = this.#form?.querySelector('input[name="id"]')?.value;
    return fromForm ? String(fromForm) : '';
  }

  /**
   * @param {string} variantId
   * @returns {PlanAllocation[]}
   */
  #allocationsForVariant(variantId) {
    if (!this.#payload?.variants || !variantId) return [];
    const list = this.#payload.variants[variantId];
    return Array.isArray(list) ? list : [];
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #sellingPlanInput() {
    return this.querySelector('[data-channel-selling-plan-input]');
  }

  /**
   * @returns {HTMLSelectElement | null}
   */
  #planSelect() {
    return this.querySelector('[data-channel-selling-plan-select]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #frequencyWrap() {
    return this.querySelector('[data-channel-frequency-wrap]');
  }

  /**
   * The label + select alone, without the subscription copy and cancel note
   * that share the frequency wrap.
   * @returns {HTMLElement | null}
   */
  #frequencyField() {
    return this.querySelector('[data-channel-frequency-field]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #oneTimePriceElement() {
    return this.querySelector('[data-channel-one-time-price]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionPriceElement() {
    return this.querySelector('[data-channel-subscription-price]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionTitleElement() {
    return this.querySelector('[data-channel-subscription-title]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionSubtitleElement() {
    return this.querySelector('[data-channel-subscription-subtitle]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionCompareElement() {
    return this.querySelector('[data-channel-subscription-compare]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionContentElement() {
    return this.querySelector('[data-channel-subscription-content]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #oneTimeCompareElement() {
    return this.querySelector('[data-channel-one-time-compare]');
  }

  /**
   * Companion mode: the plan belongs to the product referenced by this
   * product's `custom.filter_product` metafield, not to this product. The
   * unit is added one-time and the filter is added as a second line with the
   * plan, so the hidden inputs are `filter_*` and the cards show the UNIT's
   * price.
   * @returns {boolean}
   */
  get #isCompanionMode() {
    return this.dataset.companionMode === 'true';
  }

  /**
   * Companion mode only: rides the plan input's disabled state so a one-time
   * or bundle purchase adds the unit alone. Its VALUE is per main variant —
   * each variation can point at a different filter — so #syncCompanionVariant
   * rewrites it on every variant change.
   * @returns {HTMLInputElement | null}
   */
  #companionVariantInput() {
    return this.querySelector('[data-channel-filter-variant-input]');
  }

  /**
   * The filter identity for each MAIN variant, keyed by main variant id. A
   * variant with no companion filter is absent from the map, which is how
   * this component knows that variation offers no subscription.
   * @returns {Record<string, {productId: number, variantId: number, variantSku: string, title: string, collections: string}>}
   */
  #companionData() {
    if (this.#companionMap) return this.#companionMap;
    const el = this.querySelector('script[data-channel-companion-data]');
    try {
      this.#companionMap = el?.textContent ? JSON.parse(el.textContent) : {};
    } catch {
      this.#companionMap = {};
    }
    return this.#companionMap;
  }

  /**
   * Points the filter variant input at the current main variant's filter.
   * Without this a variant switch would keep adding the previously selected
   * variation's filter - the wrong subscription, silently.
   * @param {string} mainVariantId
   * @returns {boolean} whether this variant offers a subscription at all
   */
  #syncCompanionVariant(mainVariantId) {
    if (!this.#isCompanionMode) return true;
    const entry = this.#companionData()[String(mainVariantId)];
    const input = this.#companionVariantInput();
    if (input) input.value = entry?.variantId ? String(entry.variantId) : '';
    // Available only when there is also a plan to sell: the map says which
    // filter, the payload (already filtered to recurring, non-BNPL plans in
    // Liquid + JS) says whether that filter has anything subscribable.
    return !!entry?.variantId && filterRecurringAllocations(this.#allocationsForVariant(String(mainVariantId))).length > 0;
  }

  /**
   * Direct subscriptions only: `properties[_loop_priced]` marks the line as
   * Loop-priced so IOM leaves it alone (the app embed's
   * `exclude_cart_property_keys` names it). Never present in companion mode.
   * @returns {HTMLInputElement | null}
   */
  #loopPricedInput() {
    return this.querySelector('[data-channel-loop-priced-input]');
  }

  /** @returns {HTMLElement | null} */
  #bundlePriceElement() {
    return this.querySelector('[data-channel-bundle-price]');
  }

  /** @returns {HTMLElement | null} */
  #bundleCompareElement() {
    return this.querySelector('[data-channel-bundle-compare]');
  }

  /**
   * @returns {string}
   */
  #currencyCode() {
    return this.dataset.currencyCode || 'USD';
  }

  /**
   * @param {number | string | null | undefined} subunits
   * @returns {string}
   */
  #formatMoney(subunits) {
    const amount = Number(subunits);
    if (!Number.isFinite(amount)) return '';
    const major = amount / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.#currencyCode(),
      }).format(major);
    } catch {
      return `$${major.toFixed(2)}`;
    }
  }

  /**
   * @param {string} variantId
   * @returns {number | null}
   */
  #basePriceForVariant(variantId) {
    const price = this.#payload?.basePrices?.[variantId];
    return Number.isFinite(Number(price)) ? Number(price) : null;
  }

  /**
   * @param {string} variantId
   * @returns {number | null}
   */
  #baseCompareAtForVariant(variantId) {
    const value = this.#payload?.baseCompareAtPrices?.[variantId];
    return Number.isFinite(Number(value)) ? Number(value) : null;
  }

  /**
   * @param {PlanAllocation[]} allocations
   * @returns {PlanAllocation[]}
   */
  #sortAllocationsByGroupOrder(allocations) {
    if (!allocations.length) return [];
    const groupId = allocations[0].groupId;
    const order = this.#payload?.groupPlanOrder?.[groupId];
    if (!Array.isArray(order) || !order.length) {
      return [...allocations];
    }

    const rank = new Map(order.map((id, idx) => [String(id), idx]));
    return [...allocations].sort((a, b) => {
      const aIdx = rank.has(normalizePlanId(a.sellingPlanId))
        ? rank.get(normalizePlanId(a.sellingPlanId))
        : rank.get(normalizePlanId(a.sellingPlanIdNumeric));
      const bIdx = rank.has(normalizePlanId(b.sellingPlanId))
        ? rank.get(normalizePlanId(b.sellingPlanId))
        : rank.get(normalizePlanId(b.sellingPlanIdNumeric));
      const ra = aIdx ?? Number.MAX_SAFE_INTEGER;
      const rb = bIdx ?? Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return Number(a.price) - Number(b.price);
    });
  }

  /**
   * @param {string | null | undefined} html
   * @returns {{ heading: string; subtitle: string; rest: string }}
   */
  #parseDescriptionParts(html) {
    const empty = { heading: '', subtitle: '', rest: '' };
    const source = String(html ?? '').trim();
    if (!source) return empty;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
      const root = doc.body.firstElementChild;
      if (!root) return empty;
      sanitizeDescriptionTree(root);

      const h2 = root.querySelector('h2');
      const heading = h2?.textContent?.trim() ?? '';
      if (h2) h2.remove();

      let subtitle = '';
      const firstBlock = root.firstElementChild;
      if (firstBlock && /^(DIV|P|SPAN|STRONG)$/i.test(firstBlock.tagName)) {
        subtitle = firstBlock.textContent?.trim() ?? '';
        firstBlock.remove();
      }

      return {
        heading,
        subtitle,
        rest: root.innerHTML.trim(),
      };
    } catch {
      return empty;
    }
  }

  /**
   * Shows or hides the whole subscription card. In companion mode the filter
   * is resolved PER VARIANT, so a variation may have no subscription to offer
   * while its siblings do - and the selector is never re-rendered on a variant
   * change (ProductInfo swaps only the price / badge / inventory ids), so this
   * is what keeps the card honest.
   * @param {boolean} available
   */
  #setSubscriptionAvailability(available) {
    const card = this.querySelector('[data-channel-subscription-card]');
    if (card instanceof HTMLElement) card.hidden = !available;
    const radio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-channel-purchase-type="subscription"]')
    );
    if (radio) radio.disabled = !available;
  }

  /**
   * @param {'one-time' | 'subscription' | 'bundle'} purchaseType
   */
  #updateCardSelectionClasses(purchaseType) {
    const subCard = this.querySelector('[data-channel-subscription-card]');
    const oneTimeCard = this.querySelector('[data-channel-one-time-card]');
    const bundleCard = this.querySelector('[data-channel-bundle-card]');
    subCard?.classList.toggle('is-selected', purchaseType === 'subscription');
    oneTimeCard?.classList.toggle('is-selected', purchaseType === 'one-time');
    bundleCard?.classList.toggle('is-selected', purchaseType === 'bundle');
  }

  /**
   * @param {string} prefix
   * @returns {string}
   */
  #sectionPriceId(prefix) {
    const sectionId = this.dataset.sectionId ?? '';
    const productId = this.dataset.productId ?? '';
    return `${prefix}-${sectionId}-${productId}`;
  }

  /**
   * @param {string} rootId
   * @returns {HTMLElement[]}
   */
  #priceElementsIn(rootId) {
    const root = document.getElementById(rootId);
    if (!root) return [];
    if (root.classList.contains('price')) return [root];
    return /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('.price')));
  }

  /**
   * @returns {HTMLElement[]}
   */
  #productPriceElements() {
    return this.#priceElementsIn(this.#sectionPriceId('Price'));
  }

  /**
   * Sticky add-to-cart price containers (PDP only).
   * @returns {HTMLElement[]}
   */
  #stickyPriceElements() {
    return this.#priceElementsIn(this.#sectionPriceId('StickyPrice'));
  }

  /**
   * Add-to-cart button price (Concept `product-buy-price`).
   * @returns {HTMLElement[]}
   */
  #buyPriceElements() {
    const formId = this.#form?.getAttribute('id');
    if (!formId) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll(`product-buy-price[form="${formId}"]`))
    );
  }

  /**
   * @returns {HTMLElement[]}
   */
  #priceSyncTargets() {
    return [...this.#productPriceElements(), ...this.#stickyPriceElements(), ...this.#buyPriceElements()];
  }

  /**
   * @returns {number | null}
   */
  #bundleVariantCompareAtPrice() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    const raw = input?.dataset?.bundleCompareAtPrice;
    const num = Number(raw);
    return (raw && Number.isFinite(num)) ? num : null;
  }

  /**
   * Writes the bundle card's own price and compare-at price. Any IOM offer on
   * the bundle variant is painted by the engine itself on the price node - the
   * theme only ever renders Shopify's prices (the old theme-side IOM math here
   * computed savings against compare_at_price, so the card could advertise a
   * price the cart never charged).
   *
   * The bundle is a SEPARATE product, so the node carries the bundle product's
   * own ids; they are refreshed here because the bundle variant is colour-
   * matched to the selected main variant and changes with it. Once the engine
   * has painted the node (`data-iom-processed`) we leave its content alone -
   * writing the plain price back over it is what would erase the discount.
   * @param {HTMLElement | null} priceEl
   * @param {HTMLElement | null} compareEl
   */
  #updateBundleCardPricing(priceEl, compareEl) {
    const bundlePrice = this.#bundleVariantPrice();
    if (bundlePrice == null) return;

    const bundleVariantId = this.#bundleVariantId();
    if (priceEl && bundleVariantId) priceEl.setAttribute('data-variant-id', bundleVariantId);

    const bundleCompareAt = this.#bundleVariantCompareAtPrice();
    const iomProcessed = !!priceEl?.hasAttribute('data-iom-processed');
    if (priceEl && !iomProcessed) {
      priceEl.textContent = this.#formatMoney(bundlePrice);
    }
    if (compareEl) {
      // IOM renders its own struck original inside the price node, so a second
      // compare-at span here would read as two reductions.
      const showCompare = !iomProcessed && bundleCompareAt !== null && bundleCompareAt > bundlePrice;
      compareEl.textContent = showCompare ? this.#formatMoney(bundleCompareAt) : '';
    }
  }

  /**
   * Writes the resolved price into the PDP main / sticky / add-to-cart price
   * nodes.
   *
   * Each target is a `.price` element: product-price.liquid's CONTAINER (which
   * holds a regular price and, on sale, a compare-at price - two money values)
   * or Concept's single-money `product-buy-price`. Containers get a freshly
   * built single-money LEAF carrying IOM's contract attributes:
   *
   *   <div class="price price--on-sale">
   *     <span class="price__regular" data-product-type="price" ...>$59.00</span>
   *     <span class="price__sale compare-at-price relative">$79.00</span>
   *   </div>
   *
   * The IOM engine skips any matched node that contains another node matching
   * its price selector, so it never parses the container (whose two money
   * values it would read as the single string "$79.00$59.00") and paints only
   * the leaf. Replacing the leaf outright on every plan/variant change also
   * discards IOM's previous overlay along with the node it owned, so the
   * engine re-evaluates from scratch: it paints the offer price for one-time,
   * and skips while a selling plan is selected. The rebuilt leaf declares
   * `data-iom-purchase-mode="one_time"` only in the one-time / bundle cases;
   * a subscription leaf omits it so the engine can honour the form's plan.
   * That structural contract is what replaced the MutationObserver + offer
   * polling that used to fight IOM for these nodes (and double-discounted
   * subscriptions).
   *
   * @param {number | string | null | undefined} price
   * @param {number | null} [compareAt] - rendered as a strikethrough when it
   *   beats `price`.
   * @param {string} [variantId] - stamped on the leaf so IOM re-resolves the
   *   offer after a variant switch instead of keeping the load-time price.
   * @param {string} [productId] - defaults to this page's product. The bundle
   *   option posts a DIFFERENT product, and the leaf must name the one the form
   *   will add, or the engine prices it with this page's offer.
   * @param {'one-time' | 'subscription' | 'bundle'} [purchaseType] - IOM's
   *   `data-iom-purchase-mode="one_time"` is an explicit opt-out from its
   *   selling_plan fallback. Stamp it only while the form will add a one-time
   *   (or bundle) line; a subscription leaf must omit it so the engine skips.
   *   In COMPANION mode the unit line is always one-time (the plan rides the
   *   filter line), so the mode is stamped even while Subscribe is selected.
   * @param {boolean} [loopOwned] - true when the value just written is Loop's
   *   selling-plan price (a DIRECT subscription on this product). Those leaves
   *   carry `data-channel-plan-price`, which the IOM app embed's
   *   `exclude_price_selectors` names, so the engine leaves them alone and
   *   Loop's price stands. Re-decided on every rebuild: toggling to one-time
   *   drops the marker and IOM repaints the offer price.
   */
  #syncMainProductPrice(price, compareAt = null, variantId = '', productId = '', purchaseType = '', loopOwned = false) {
    const formattedPrice = this.#formatMoney(price);
    if (!formattedPrice) return;

    const showCompare = compareAt != null && price != null && Number(compareAt) > Number(price);
    const formattedCompare = showCompare ? this.#formatMoney(compareAt) : '';
    const resolvedProductId = String(productId || this.dataset.productId || '');
    // Companion mode: Subscribe still adds the UNIT one-time, so its price is
    // IOM's to paint. Only a DIRECT subscription leaf omits the mode.
    const iomOneTime = purchaseType !== 'subscription' || this.#isCompanionMode;

    for (const target of this.#priceSyncTargets()) {
      if (!(target instanceof HTMLElement)) continue;

      // product-buy-price is itself a single-money leaf - IOM reads it directly.
      if (target.tagName === 'PRODUCT-BUY-PRICE') {
        target.textContent = formattedPrice;
        if (Number.isFinite(Number(price))) {
          target.setAttribute('data-price', String(price));
        }
        if (loopOwned) target.setAttribute('data-channel-plan-price', '');
        else target.removeAttribute('data-channel-plan-price');
        continue;
      }

      const existingLeaf = target.querySelector('.price__regular');
      // Same contract as the one-time / bundle cards: IOM already draws the
      // struck MSRP inside the leaf. A theme compare-at next to it is a
      // second $149 on affiliate shower / filter stickies.
      const iomPainted = !!(
        existingLeaf?.hasAttribute('data-iom-processed') ||
        existingLeaf?.querySelector('.iom-original-price, .iom-discount-price')
      );
      // Subscriptions never get an IOM overlay, so always write the plan's
      // compare-at — even when the leaf we are replacing still has one from
      // the previous one-time selection.
      const writeCompare = showCompare && !(iomOneTime && iomPainted);

      const leaf = document.createElement('span');
      leaf.className = 'price__regular whitespace-nowrap';
      leaf.setAttribute('data-product-type', 'price');
      // Sticky / main price sit outside the purchase radios. Without an
      // explicit mode the engine falls back to the form's selling_plan: it
      // paints a one-time offer when that input is empty, and skips while a
      // plan is selected. `one_time` is only for the one-time / bundle
      // cases — stamping it on a subscription leaf is what made the sticky
      // show the offer while add-to-cart still posted the plan.
      if (iomOneTime) leaf.setAttribute('data-iom-purchase-mode', 'one_time');
      // Loop owns this number - keep IOM off it (app embed
      // `exclude_price_selectors: [data-channel-plan-price]`).
      if (loopOwned) leaf.setAttribute('data-channel-plan-price', '');
      if (resolvedProductId) leaf.setAttribute('data-product-id', resolvedProductId);
      if (variantId) leaf.setAttribute('data-variant-id', variantId);
      leaf.textContent = formattedPrice;

      if (existingLeaf) existingLeaf.replaceWith(leaf);
      else target.insertBefore(leaf, target.firstChild);

      // Older Liquid stamped one_time on the sticky hosts. IOM can inherit
      // that mode from an ancestor, so drop any leftover while a plan is
      // selected. Do not write it back onto the hosts: the leaf is enough.
      if (!iomOneTime) {
        target.removeAttribute('data-iom-purchase-mode');
        target.closest('product-sticky-form')?.removeAttribute('data-iom-purchase-mode');
        target.closest('[id^="StickyPrice-"]')?.removeAttribute('data-iom-purchase-mode');
      }

      target.classList.toggle('price--on-sale', writeCompare);

      const sale = target.querySelector('.price__sale');
      // Match product-price.liquid: a relatively-positioned span, not <s>.
      // <s> draws a native line-through on top of the theme slash, and
      // without `relative` the slash's containing block is the sticky form.
      if (sale instanceof HTMLElement) {
        sale.textContent = writeCompare ? formattedCompare : '';
        sale.toggleAttribute('hidden', !writeCompare);
        sale.classList.add('inline-flex', 'items-center', 'h-auto', 'relative');
      } else if (writeCompare) {
        const created = document.createElement('span');
        created.className = 'price__sale compare-at-price inline-flex items-center h-auto relative';
        created.textContent = formattedCompare;
        target.appendChild(created);
      }
    }
  }

  /**
   * @param {PlanAllocation | undefined} plan
   * @param {string} variantId
   * @param {'one-time' | 'subscription' | 'bundle'} purchaseType
   */
  #updateVisiblePricing(plan, variantId, purchaseType) {
    const oneTimeEl = this.#oneTimePriceElement();
    const subEl = this.#subscriptionPriceElement();
    const subTitleEl = this.#subscriptionTitleElement();
    const subSubtitleEl = this.#subscriptionSubtitleElement();
    const subCompareEl = this.#subscriptionCompareElement();
    const subContentEl = this.#subscriptionContentElement();
    const oneTimeCompareEl = this.#oneTimeCompareElement();
    const bundlePriceEl = this.#bundlePriceElement();
    const bundleCompareEl = this.#bundleCompareElement();

    const basePrice = this.#basePriceForVariant(variantId);
    const baseCompareAt = this.#baseCompareAtForVariant(variantId);
    // Companion mode: the plan belongs to the filter product, so the main
    // price and both cards keep showing the UNIT's own price.
    const companion = this.#isCompanionMode;
    // The main price shows a Loop plan price only for a DIRECT subscription on
    // this product - the same condition that swaps basePrice for plan.price.
    const loopOwnsMainPrice = purchaseType === 'subscription' && !!plan && !companion;

    let mainDisplayPrice = basePrice;
    let mainCompareAt = null;
    let mainVariantId = variantId;
    let mainProductId = '';
    if (loopOwnsMainPrice && plan) {
      mainDisplayPrice = plan.price;
      if (plan.compareAtPrice != null && Number(plan.compareAtPrice) > Number(plan.price)) {
        mainCompareAt = Number(plan.compareAtPrice);
      }
    } else if (purchaseType === 'bundle') {
      const bundlePrice = this.#bundleVariantPrice();
      const bundleCompareAt = this.#bundleVariantCompareAtPrice();
      mainDisplayPrice = bundlePrice;
      if (bundleCompareAt != null && bundlePrice != null && bundleCompareAt > bundlePrice) {
        mainCompareAt = bundleCompareAt;
      }
      // The form posts the bundle variant while this option is selected, so the
      // price below the cards belongs to the bundle product, not to this page's.
      mainVariantId = this.#bundleVariantId() || variantId;
      mainProductId = String(this.dataset.bundleProductId || '');
    } else if (baseCompareAt !== null && basePrice !== null && baseCompareAt > basePrice) {
      mainCompareAt = baseCompareAt;
    }
    this.#syncMainProductPrice(
      mainDisplayPrice,
      mainCompareAt,
      mainVariantId,
      mainProductId,
      purchaseType,
      loopOwnsMainPrice
    );

    this.#updateBundleCardPricing(bundlePriceEl, bundleCompareEl);

    // The one-time card's price node carries IOM's contract attributes
    // (data-product-type="price" + ids), so the engine paints the one-time
    // offer price there itself. We only ever write Shopify's undiscounted
    // price, keep the ids fresh so IOM re-resolves after a variant switch, and
    // never touch its injected overlay (theme policy: no IOM DOM surgery).
    oneTimeEl?.setAttribute('data-variant-id', variantId);
    oneTimeEl?.setAttribute('data-product-id', String(this.dataset.productId || ''));

    const oneTimeIomProcessed = !!oneTimeEl?.hasAttribute('data-iom-processed');
    if (oneTimeEl && !oneTimeIomProcessed) {
      oneTimeEl.textContent = basePrice !== null ? this.#formatMoney(basePrice) : '';
    }
    if (oneTimeCompareEl) {
      // IOM renders its own struck original inside the price node, so a second
      // compare-at span here would read as two reductions.
      const showCompare =
        !oneTimeIomProcessed && baseCompareAt !== null && basePrice !== null && baseCompareAt > basePrice;
      oneTimeCompareEl.textContent = showCompare ? this.#formatMoney(baseCompareAt) : '';
    }

    if (subEl) {
      // Companion mode: this card leads with the UNIT price - the same value
      // the one-time card shows - and the node carries IOM's attributes, so
      // keep data-variant-id fresh or the engine keeps painting the load-time
      // variant's offer price after a variant switch. Stop writing once it has
      // painted, exactly as the one-time card does. Outside companion mode the
      // node holds the PLAN price and IOM is excluded from it entirely
      // (data-channel-plan-price), so the theme always owns the text.
      if (companion) {
        subEl.setAttribute('data-variant-id', variantId);
        subEl.setAttribute('data-product-id', String(this.dataset.productId || ''));
        if (!subEl.hasAttribute('data-iom-processed')) {
          subEl.textContent = basePrice !== null ? this.#formatMoney(basePrice) : '';
        }
      } else {
        subEl.textContent = plan ? this.#formatMoney(plan.price) : '';
      }
    }

    const parsed = this.#parseDescriptionParts(plan?.description);
    if (subTitleEl) {
      const fallback = subTitleEl.dataset.defaultLabel || '';
      subTitleEl.textContent = parsed.heading || plan?.groupName || fallback;
    }
    if (subSubtitleEl) {
      // A description that carries its own <h2> owns the card's copy: when it
      // gives no subtitle, it gets none, rather than the generic default
      // under a title it wrote (e.g. a beside-title badge layout).
      const fallback = parsed.heading ? '' : subSubtitleEl.dataset.defaultSubtitle || '';
      const subtitle = parsed.subtitle || fallback;
      subSubtitleEl.textContent = subtitle;
      subSubtitleEl.hidden = !subtitle;
    }
    // Same ownership rule for the generic cancel note: a description with its
    // own <h2> carries its own terms, so the theme's line would repeat them.
    const cancelNoteEl = this.querySelector('[data-channel-cancel-note]');
    if (cancelNoteEl) {
      cancelNoteEl.hidden = !!parsed.heading;
    }
    if (subCompareEl) {
      // Companion mode: the card shows the unit price, so a filter-plan
      // compare-at would mislead - and IOM draws its own struck original
      // inside the price node. Leave it empty.
      //
      // Direct mode: strike the ONE-TIME price for this variant - what the
      // shopper pays by NOT subscribing - so the card reads "$171.99 $146.19"
      // and the plan's saving is legible. This matters because IOM is excluded
      // from this card's price node, so it no longer paints a strike-through
      // of its own; without this the plan price sits alone with nothing to
      // compare against. The variant's own compare-at is only a fallback for a
      // missing base price: preferring it would overstate the saving on a
      // product that is already marked down, because the plan discount comes
      // off the sale price, not the MSRP.
      const oneTimePrice = basePrice ?? plan?.compareAtPrice ?? null;
      const showCompare =
        !companion &&
        oneTimePrice !== null &&
        plan?.price != null &&
        Number(oneTimePrice) > Number(plan.price);
      subCompareEl.textContent = showCompare ? this.#formatMoney(oneTimePrice) : '';
    }
    if (subContentEl) {
      subContentEl.innerHTML = parsed.rest;
    }
    this.#updateCardSelectionClasses(purchaseType);
  }

  /**
   * @returns {'one-time' | 'subscription' | 'bundle'}
   */
  #purchaseType() {
    if (this.dataset.requiresSellingPlan === 'true') {
      return 'subscription';
    }
    const checked = this.querySelector('[data-channel-purchase-type]:checked');
    const v = checked?.getAttribute('value');
    if (v === 'subscription') return 'subscription';
    if (v === 'bundle') return 'bundle';
    return 'one-time';
  }

  /**
   * @param {PlanAllocation[]} groupAllocs
   * @param {string} defaultPlanId
   */
  #selectDefaultPlanInGroup(groupAllocs, defaultPlanId) {
    const usable = filterRecurringAllocations(groupAllocs);
    if (!usable.length) return undefined;

    const explicitDefault = usable.find((a) => a.isDefault === true);
    if (explicitDefault) return explicitDefault;

    const normDefault = normalizeKey(defaultPlanId);
    if (normDefault) {
      const match = usable.find(
        (a) =>
          normalizeKey(a.sellingPlanId) === normDefault ||
          normalizeKey(a.sellingPlanIdNumeric) === normDefault ||
          a.sellingPlanId === defaultPlanId ||
          a.sellingPlanIdNumeric === defaultPlanId
      );
      if (match) return match;
    }
    const orderedByPosition = [...usable].sort((a, b) => {
      const pa = Number(a.position ?? 0);
      const pb = Number(b.position ?? 0);
      if (pa !== pb) return pa - pb;
      return 0;
    });
    if (orderedByPosition[0] && Number(orderedByPosition[0].position ?? 0) > 0) {
      return orderedByPosition[0];
    }

    const ordered = this.#sortAllocationsByGroupOrder(usable);
    return ordered[0] ?? pickCheapestAllocation(usable);
  }

  /**
   * @param {string} variantId
   * @returns {{ allocations: PlanAllocation[]; selected: PlanAllocation | undefined }}
   */
  #resolveSubscriptionSelection(variantId) {
    const all = this.#allocationsForVariant(variantId);
    const recurring = filterRecurringAllocations(all);
    const defaultPlanId = this.dataset.defaultSellingPlanId ?? '';

    if (!recurring.length) {
      return { allocations: [], selected: undefined };
    }

    // Plan choice is a property of the PRODUCT, not of where the shopper came
    // from. `#selectDefaultPlanInGroup` applies, in order: the plan Shopify
    // marks selected, the `custom.default_selling_plan_id` metafield, the
    // lowest `position`, the Liquid `groupPlanOrder`, then price.
    //
    // A product can carry more than one group (the Carafe's two filter
    // variations), so the winning plan's group is what fills the frequency
    // dropdown - picking across groups would offer frequencies that belong to
    // a plan the form is not posting.
    const selected = this.#selectDefaultPlanInGroup(recurring, defaultPlanId) ?? pickCheapestAllocation(recurring);
    if (!selected) {
      return { allocations: [], selected: undefined };
    }

    const sameGroup = recurring.filter((a) => normalizeKey(a.groupName) === normalizeKey(selected.groupName));
    return { allocations: sameGroup.length ? sameGroup : recurring, selected };
  }

  /**
   * @param {PlanAllocation[]} allocations
   */
  #fillSelectOptions(allocations) {
    const select = this.#planSelect();
    if (!select) return;
    select.innerHTML = '';
    const usable = filterRecurringAllocations(allocations);
    const sorted = this.#sortAllocationsByGroupOrder(usable);
    for (const a of sorted) {
      const opt = document.createElement('option');
      opt.value = a.sellingPlanId;
      opt.textContent = a.deliveryFrequency || a.sellingPlanName || a.sellingPlanId;
      select.appendChild(opt);
    }
    // Nothing to choose between with a single frequency, so don't ask. The
    // select itself stays populated and keeps feeding #syncSellingPlanInput.
    const field = this.#frequencyField();
    if (field) field.hidden = select.options.length < 2;
  }

  /**
   * @param {string | number} sellingPlanId
   */
  #syncSellingPlanInput(sellingPlanId) {
    const input = this.#sellingPlanInput();
    if (!input) return;
    const previous = input.value;
    input.value = sellingPlanId;
    input.disabled = !sellingPlanId;
    // IOM watches input[name='selling_plan'] to decide whether a line is a
    // subscription (it skips subscription discounts). A bare `.value` write is
    // invisible to it, so mirror the value onto the ATTRIBUTE and fire a
    // bubbling `change`. Never dispatch `variant:change` here - this component
    // listens for that on the form and would re-enter #refreshPlanState.
    if (sellingPlanId) input.setAttribute('value', String(sellingPlanId));
    else input.removeAttribute('value');

    // Companion mode: the filter variant input rides along - disabled (and so
    // excluded from FormData) whenever no plan is selected, so a one-time or
    // bundle purchase adds only the unit.
    const companionVariantInput = this.#companionVariantInput();
    if (companionVariantInput) companionVariantInput.disabled = !sellingPlanId;

    // Direct mode: the Loop-priced marker rides the same toggle, so it is only
    // submitted when a plan is actually selected. Never present in companion
    // mode - that line stays IOM's to discount.
    const loopPricedInput = this.#loopPricedInput();
    if (loopPricedInput) loopPricedInput.disabled = !sellingPlanId;

    if (String(previous) !== String(input.value)) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Companion mode only: dynamic payment buttons (Shop Pay etc.) can only
   * express the form's single item - with the filter subscription selected
   * they would silently check out the unit alone, so hide them. The dataset
   * flag guard ensures we never unhide a block hidden for another reason
   * (e.g. sold out).
   * @param {'one-time' | 'subscription' | 'bundle'} purchaseType
   */
  #syncAcceleratedCheckout(purchaseType) {
    if (!this.#isCompanionMode) return;
    const accelerated = this.#form?.querySelector('.shopify-payment-button');
    if (!(accelerated instanceof HTMLElement)) return;

    if (purchaseType === 'subscription') {
      if (!accelerated.hidden) {
        accelerated.hidden = true;
        accelerated.dataset.channelCompanionHidden = 'true';
      }
    } else if (accelerated.dataset.channelCompanionHidden === 'true') {
      accelerated.hidden = false;
      delete accelerated.dataset.channelCompanionHidden;
    }
  }

  /**
   * Reads the bundle variant ID from the checked bundle radio input.
   * @returns {string}
   */
  #bundleVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    return input?.dataset?.bundleVariantId ?? this.dataset.bundleVariantId ?? '';
  }

  /**
   * Looks up the bundle variant that matches a given main-product variant
   * (matched by color in the Liquid payload), if any.
   * @param {string} mainVariantId
   * @returns {BundleVariantMatch | null}
   */
  #bundleVariantForMain(mainVariantId) {
    const map = this.#payload?.bundleVariants;
    if (!map || !mainVariantId) return null;
    return map[mainVariantId] ?? null;
  }

  /**
   * Syncs the bundle radio's dataset (variant id + pricing) to the bundle
   * variant matching the currently selected main-product variant. Keeps the
   * bundle card price accurate and ensures add-to-cart swaps in the same-color
   * bundle variant rather than always the default one.
   * @param {string} mainVariantId
   */
  #syncBundleVariantForMain(mainVariantId) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    if (!input) return;
    const matched = this.#bundleVariantForMain(mainVariantId);
    if (!matched || matched.id == null) return;
    input.dataset.bundleVariantId = String(matched.id);
    if (matched.price != null) {
      input.dataset.bundlePrice = String(matched.price);
    }
    if (matched.compareAtPrice != null) {
      input.dataset.bundleCompareAtPrice = String(matched.compareAtPrice);
    } else {
      input.dataset.bundleCompareAtPrice = '';
    }
  }

  /**
   * Reads the bundle variant price (in subunits) from the checked bundle radio input.
   * @returns {number | null}
   */
  #bundleVariantPrice() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-channel-purchase-type="bundle"]'));
    const raw = input?.dataset?.bundlePrice;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }

  /**
   * Swaps the form's hidden variant ID input to a different variant.
   * Stores the original so it can be restored later.
   * @param {string} variantId
   */
  #swapFormVariantId(variantId) {
    const input = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="id"]'));
    if (!input) return;
    if (!this.#savedOriginalVariantId) {
      this.#savedOriginalVariantId = input.value;
    }
    input.value = variantId;
  }

  /**
   * Restores the form's hidden variant ID to the original product variant.
   */
  #restoreFormVariantId() {
    if (!this.#savedOriginalVariantId) return;
    const input = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="id"]'));
    if (input) {
      input.value = this.#savedOriginalVariantId;
    }
    this.#savedOriginalVariantId = null;
  }

  /**
   * Returns the main-product variant ID even when the form input has been
   * swapped to a bundle variant.
   * @returns {string}
   */
  #originalVariantId() {
    return this.#savedOriginalVariantId ?? this.#currentVariantId();
  }

  async #refreshPlanState() {
    const frequencyWrap = this.#frequencyWrap();
    const select = this.#planSelect();

    // Point the filter inputs at THIS variant's filter before anything reads
    // them, and hide the subscription card when this variation has none. Must
    // run before #purchaseType(), which reads the radios this may disable.
    const companionAvailable = this.#syncCompanionVariant(this.#originalVariantId());
    if (this.#isCompanionMode) {
      this.#setSubscriptionAvailability(companionAvailable);
      if (!companionAvailable) {
        const oneTimeRadio = /** @type {HTMLInputElement | null} */ (
          this.querySelector('[data-channel-purchase-type="one-time"]')
        );
        const bundleChecked = this.querySelector('[data-channel-purchase-type="bundle"]:checked');
        if (oneTimeRadio && !bundleChecked) oneTimeRadio.checked = true;
      }
    }

    const purchaseType = this.#purchaseType();

    if (purchaseType === 'bundle') {
      const mainVariantId = this.#originalVariantId();
      this.#syncBundleVariantForMain(mainVariantId);
      const subscriptionPreview = this.#resolveSubscriptionSelection(mainVariantId).selected;
      frequencyWrap?.setAttribute('hidden', '');
      select && (select.innerHTML = '');
      this.#syncSellingPlanInput('');

      const bundleVid = this.#bundleVariantId();
      if (bundleVid) {
        this.#swapFormVariantId(bundleVid);
      }

      this.#updateVisiblePricing(subscriptionPreview, mainVariantId, 'bundle');
      this.#syncAcceleratedCheckout('bundle');
      return;
    }

    this.#restoreFormVariantId();
    const variantId = this.#currentVariantId();
    this.#syncBundleVariantForMain(variantId);

    if (purchaseType === 'one-time') {
      const subscriptionPreview = this.#resolveSubscriptionSelection(variantId).selected;
      frequencyWrap?.setAttribute('hidden', '');
      select && (select.innerHTML = '');
      this.#syncSellingPlanInput('');
      this.#updateVisiblePricing(subscriptionPreview, variantId, 'one-time');
      this.#syncAcceleratedCheckout('one-time');
      return;
    }

    frequencyWrap?.removeAttribute('hidden');

    const { allocations, selected } = this.#resolveSubscriptionSelection(variantId);
    this.#fillSelectOptions(allocations);

    let plan = selected;
    if (plan && select) {
      const matchOption = Array.from(select.options).some((o) => allocationMatchesSellingPlanId(plan, o.value));
      if (!matchOption) {
        plan = pickCheapestAllocation(allocations);
      }
    }

    if (select && plan) {
      select.value = plan.sellingPlanId;
    }

    if (plan) {
      this.#syncSellingPlanInput(plan.sellingPlanId);
      this.#updateVisiblePricing(plan, variantId, 'subscription');
      this.#syncAcceleratedCheckout('subscription');
    } else {
      const oneTimeRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-channel-purchase-type="one-time"]')
      );
      if (oneTimeRadio) {
        oneTimeRadio.checked = true;
        frequencyWrap?.setAttribute('hidden', '');
        select && (select.innerHTML = '');
      }
      this.#syncSellingPlanInput('');
      this.#updateVisiblePricing(undefined, variantId, this.#purchaseType());
      this.#syncAcceleratedCheckout(this.#purchaseType());
    }
  }

  /** @param {Event} event */
  #onVariantUpdate(event) {
    const detail = event?.detail ?? event;
    const productId = detail?.data?.productId;
    if (productId && String(productId) !== String(this.dataset.productId ?? '')) {
      return;
    }
    // The product form's own variant:update handler resets input[name="id"] to
    // the new main variant. Drop any saved swap so #originalVariantId() reads
    // the new main variant; the bundle branch re-swaps to the matching variant.
    this.#savedOriginalVariantId = null;
    // Close the one-frame window before the rAF refresh below: a submit fired
    // in that frame must never post the PREVIOUS variant's filter (the wrong
    // subscription, silently), so re-point the companion inputs now from the
    // event's own variant. The full UI refresh still waits a frame for the
    // product form to reset input[name="id"].
    const nextVariant = detail?.data?.variant ?? detail?.variant;
    if (nextVariant?.id && this.#isCompanionMode) {
      const nextId = String(nextVariant.id);
      const available = this.#syncCompanionVariant(nextId);
      if (this.#purchaseType() === 'subscription') {
        const { selected } = this.#resolveSubscriptionSelection(nextId);
        const planId = available && selected ? selected.sellingPlanId ?? selected.sellingPlanIdNumeric ?? '' : '';
        this.#syncSellingPlanInput(planId);
      }
    }
    window.requestAnimationFrame(() => {
      void this.#refreshPlanState();
    });
  }

  #onPurchaseTypeChange() {
    void this.#refreshPlanState();
  }

  #onSelectChange() {
    const select = this.#planSelect();
    const id = select?.value ?? '';
    this.#syncSellingPlanInput(id);
    const variantId = this.#currentVariantId();
    const all = this.#allocationsForVariant(variantId);
    const plan = all.find((a) => allocationMatchesSellingPlanId(a, id));
    this.#updateVisiblePricing(plan, variantId, 'subscription');
  }
}

if (!customElements.get('channel-selling-plan-root')) {
  customElements.define('channel-selling-plan-root', ChannelSellingPlanRoot);
}
